'use client'

import { type MouseEventHandler, type ReactNode, useCallback, useEffect, useState } from 'react'

import cn from 'clsx'
import { SearchField } from '@/components/future/search-field/search-field'

import { Box } from '@/components/Box/Box'

import { SearchResult } from './SearchResult/SearchResult'

import css from './AutocompleteSearch.module.css'

export type AutocompleteSearchResult = {
    id: string
    title: string
    href?: string
    onClick?: MouseEventHandler<HTMLElement>
    description?: ReactNode
}
export type AutocompleteSearchProps = {
    placeholder: string
    htmlFor: string
    value?: string
    searchLabel: string
    clearLabel: string
    searchResults?: AutocompleteSearchResult[]
    onChange?: (searchedText: string) => void
    onSubmit?: (searchedText: string) => void /** When the user hit Enter */
    applyHeaderStyle?: boolean /* Shows search results with a fixed width. */
}
export function AutocompleteSearch({
    value,
    placeholder,
    htmlFor,
    searchLabel,
    clearLabel,
    onChange,
    searchResults = [],
    onSubmit,
    applyHeaderStyle = false,
}: AutocompleteSearchProps) {
    const [searchText, setSearchText] = useState<string | undefined>(value)
    const [searchFieldFocused, setSearchFieldFocused] = useState(false)
    const [searchResultsFocused, setSearchResultsFocused] = useState(false)
    const [searchInitiated, setSearchInitiated] = useState(false)

    useEffect(() => {
        setSearchText(value)
    }, [value])

    function onSearchBlur() {
        setTimeout(() => {
            setSearchFieldFocused(false)
        }, 200)
    }

    const showSearchResults = useCallback(
        () => (!searchFieldFocused && !searchResultsFocused) || searchResults.length === 0,
        [searchFieldFocused, searchResultsFocused, searchResults],
    )

    return (
        <Box className={css.autocompleteSearch}>
            <SearchField
                id={htmlFor}
                value={searchText ? searchText : ''}
                aria-label={placeholder}
                placeholder={placeholder}
                onFocus={() => setSearchFieldFocused(true)}
                onBlur={onSearchBlur}
                searchLabel={searchLabel}
                clearLabel={clearLabel}
                onSearch={(query) => {
                    setSearchInitiated(true)
                    onSubmit?.(query)
                }}
                onChange={(e) => onChange?.(e.currentTarget.value)}
                disabled={searchInitiated}
            />

            <ul
                className={cn({
                    [css.hide]: showSearchResults(),
                    [css.headerStyle]: applyHeaderStyle,
                })}
            >
                {searchResults.map((result) => (
                    <li key={result.id}>
                        <SearchResult
                            searchResult={result}
                            onFocus={() => setSearchResultsFocused(true)}
                            onBlur={() => setSearchResultsFocused(false)}
                        />
                    </li>
                ))}
            </ul>
        </Box>
    )
}
